import { useState } from "react";
import { ErrorText, Field, Panel, Spinner } from "../../components/ui";
import { fmtAmount, fmtDate, fmtPct, fmtPrice, fmtRate } from "../../lib/format";
import { useClientDetail, useClientSearch } from "./hooks";
import type { ClientPortfolio, ClientSummary } from "./api";

export function ClientsPage() {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<ClientSummary | null>(null);

  const results = useClientSearch(q);
  const detail = useClientDetail(selected?.id ?? null);

  return (
    <Panel title="Clients — portefeuilles (lecture seule)">
      <p className="muted">
        Recherche un client par email ou nom pour consulter ses portefeuilles et positions.
        Vue en lecture seule, aucune modification possible depuis cet écran.
      </p>

      <div className="inline-form">
        <Field
          label="Rechercher (email ou nom)"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setSelected(null);
          }}
          placeholder="ex. awa@example.com"
        />
      </div>

      <ErrorText error={results.error} />
      {q.trim() && (
        <div className="table-wrap">
          {results.isLoading ? (
            <Spinner />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Nom</th>
                  <th>Téléphone</th>
                  <th>KYC</th>
                  <th>Inscrit le</th>
                </tr>
              </thead>
              <tbody>
                {results.data?.map((c) => (
                  <tr
                    key={c.id}
                    className={`row-clickable ${selected?.id === c.id ? "row-selected" : ""}`}
                    onClick={() => setSelected(c)}
                  >
                    <td>{c.email}</td>
                    <td>{c.full_name ?? "—"}</td>
                    <td>{c.phone ?? "—"}</td>
                    <td>{c.kyc_status}</td>
                    <td>{fmtDate(c.created_at)}</td>
                  </tr>
                ))}
                {results.data?.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      Aucun client trouvé.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {selected && (
        <>
          <h3 className="panel-subhead">
            Portefeuilles de {selected.full_name ?? selected.email}
          </h3>
          <ErrorText error={detail.error} />
          {detail.isLoading ? (
            <Spinner />
          ) : (
            detail.data?.portfolios.map((p) => <PortfolioBlock key={p.id} portfolio={p} />)
          )}
          {detail.data?.portfolios.length === 0 && (
            <p className="muted">Ce client n'a aucun portefeuille.</p>
          )}
        </>
      )}
    </Panel>
  );
}

function PortfolioBlock({ portfolio }: { portfolio: ClientPortfolio }) {
  return (
    <div className="client-portfolio">
      <div className="client-portfolio-head">
        <strong>
          {portfolio.name} · {portfolio.kind.toUpperCase()}
        </strong>
        <span className="muted">
          Rendement cible : {fmtRate(portfolio.targetReturn)}
          {" · "}Valeur totale : {fmtAmount(portfolio.summary.total_value)} {portfolio.baseCurrency}
          {" · "}PV latente : {fmtAmount(portfolio.summary.unrealized_pnl)}
          {" · "}PV réalisée : {fmtAmount(portfolio.summary.realized_pnl)}
          {" · "}Perf. globale : {fmtPct(portfolio.summary.total_return_pct)}
          {" · "}Rendement pondéré : {fmtPct(portfolio.summary.weighted_annualized_return)}
        </span>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Titre</th>
              <th>Qté</th>
              <th>CUMP</th>
              <th>Cours</th>
              <th>Valeur</th>
              <th>PV latente %</th>
              <th>XIRR</th>
            </tr>
          </thead>
          <tbody>
            {portfolio.positions.map((pos) => (
              <tr key={pos.security_id}>
                <td>
                  {pos.ticker} — {pos.name}
                </td>
                <td>{fmtAmount(pos.quantity)}</td>
                <td>{fmtPrice(pos.cump)}</td>
                <td>{pos.market_price ? fmtPrice(pos.market_price) : "—"}</td>
                <td>{fmtAmount(pos.market_value)}</td>
                <td>{fmtPct(pos.unrealized_pnl_pct)}</td>
                <td>{fmtPct(pos.annualized_return)}</td>
              </tr>
            ))}
            {portfolio.positions.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  Aucune position.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
